import {
  AfterViewChecked,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { catchError, forkJoin, map, Observable, of, Subscription, switchMap, tap } from 'rxjs';
import { ModalComponent } from 'src/app/utils/modal/modal.component';
import { BookReaderService } from '../../services/book-reader.service';
import { BookReaderApiV3Service } from '../../services/v3/book-reader-api-v3.service';
import { BookContent, BookMetadataV2 } from '../../model/book-model';
import { Quiz, QuizParser } from '../../model/quiz';
import { SpinnerService } from 'src/app/utils/services/spinner.service';
import { ActivatedRoute, Router } from '@angular/router';
import { EncryptionService } from '../../services/encryption.service';
import { TranslateService } from '@ngx-translate/core';
import { BookPageMarkersUtils } from 'src/app/col/utils/book-page-markers-utils';

declare var bootstrap: any;

// Manifest entry holding the quiz exercises; served via /content, never navigated to.
const TGCHECKS_TYPE = 'TGCHECKS';

interface ModalChartImage {
  alt: string;
  url: string;
}

class BreakpointGroup {
  scrollOffset = 0;
  constructor(public items: Array<any>) {
  }
}

/**
 * A single navigable unit in a ver-2 book, flattened from the manifest `contents` in order:
 *  - an anchor entry (intro, charts) contributes one unit per anchor (kind 'anchor'),
 *  - a page-range entry (lesson/dictation) contributes one unit per page (kind 'page').
 * A combined book spans several `;`-joined sub-products, so each unit also records the sub-product
 * `path` it belongs to (used to route the content fetch) and the raw `anchor` sent to the backend.
 * `key` is the unique URL/`anchor`-param id (anchor keys are prefixed per sub-product to stay unique).
 */
interface ReaderUnit {
  key: string;
  anchor: string;
  kind: 'anchor' | 'page';
  path: string;
}

/**
 * Reader for ver 2 books. Navigation follows the manifest `contents` order: anchors are stepped one
 * by one, page ranges are stepped page by page (+1 / -1). The current unit's key (anchor or page
 * number) is reflected in the URL `anchor` query param.
 */
@Component({
  selector: 'app-web-browser-v2',
  templateUrl: './web-browser-v2.component.html',
  styleUrls: ['./web-browser-v2.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class WebBrowserV2Component implements OnInit, OnDestroy, AfterViewChecked {
  public static JUMP_TO_THE_FIRST = -Number.MAX_SAFE_INTEGER;
  public static JUMP_TO_THE_LAST = Number.MAX_SAFE_INTEGER;

  ebookContent: SafeHtml
  private _ebookPath: string

  // Flattened navigation sequence (manifest order) and the cursor into it.
  units: ReaderUnit[] = []
  currentIndex: number = 0

  // Page-number bounds + selection for the "jump to page" modal (pages only, not anchors).
  pageSelection: number
  firstPage: number = 0
  lastPage: number = 0

  // Table of contents grouped by content type (rendered as a Bootstrap accordion in the TOC modal).
  // Each entry carries its navigation `key` (unit key) so TOC clicks resolve across sub-products.
  tocGroups: { type: string; label: string; entries: { entry: BookContent; key: string }[] }[] = []

  currentBreakpoint = 0
  // When navigating backwards into page-less content, scroll it to the end once it has loaded.
  private pendingScrollToEnd = false;
  // When a unit is opened (next page, table of contents, jump to page), scroll it to the top once it has loaded
  // — the scroll container is shared, so otherwise the position of the previous unit would carry over.
  private pendingScrollToTop = false;
  private breakpointGroups: Array<BreakpointGroup>;
  isBookSizeSettingsShown: boolean = false
  zoom: number
  private tooltipReady = false;
  private tooltipInstances: any[] = [];
  isFullscreen = false;

  // Teacher Guide Plus quizzes (<tgcheck id="...">): the exercises of a book are loaded once from its
  // TGCHECKS content entry and kept per product path.
  selectedQuiz: Quiz | null = null;
  private quizzesByPath = new Map<string, Map<string, Quiz>>();
  private quizSubscription?: Subscription;

  // Clip shown in the video modal; cleared when the modal closes so the player is destroyed and stops.
  selectedVideoSrc: string | null = null;
  private videoModalSubscription?: Subscription;

  sizeSubscription: Subscription
  private bookUpdateObserver: MutationObserver;

  @ViewChild('jumpToPageModal', { static: true })
  jumpToPageModal: ModalComponent;
  @ViewChild('tocModal', { static: true })
  tocModal: ModalComponent;
  @ViewChild('chartModal', { static: true })
  chartModal: ModalComponent;
  @ViewChild('quizModal', { static: true })
  quizModal: ModalComponent;
  @ViewChild('videoModal', { static: true })
  videoModal: ModalComponent;
  @ViewChild('bookContainer', {static: true})
  bookContainer: ElementRef;
  @ViewChild('contentContainer', {static: true})
  contentContainer: ElementRef;
  @ViewChild('fullscreenContainer', { static: true })
  fullscreenContainer: ElementRef;

  // ver-2 metadata of the FIRST sub-product, already fetched by the reader page to detect the version.
  // Reused as-is; metadata for any further `;`-joined sub-products (combined books) is fetched here.
  @Input()
  metadata: BookMetadataV2

  selectedChartImages: ModalChartImage[] = [];
  selectedChartTitle: string = 'Chart';
  // Metadata per sub-product, used on chart click to resolve the ref-id and its image sources.
  private metadataByPath = new Map<string, BookMetadataV2>();
  private chartObjectUrls: string[] = [];
  private chartResourcesSubscription: Subscription;

  @Input()
  set ebookPath(value: string){
    if(value) {
      this._ebookPath = value
    }
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (!event || !event.code) {
      return;
    }
    switch (event.code) {
      case 'ArrowLeft':
        this.moveToPreviousPage()
        break
      case 'ArrowRight':
        this.moveToNextPage()
        break
      case 'ArrowDown':
        this.scrollDown(event)
        break
      case 'ArrowUp':
        this.scrollUp(event)
        break
    }
  }
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.updateBreakpoints()
  }
  @HostListener('document:fullscreenchange', ['$event'])
  onFullscreenChange() {
    const doc = document as any;
    this.isFullscreen = !!(doc.fullscreenElement || doc.webkitFullscreenElement);
    this.tooltipInstances.forEach(t => t.dispose());
    this.tooltipInstances = [];
    this.tooltipReady = false;
    setTimeout(() => {
      this.updateBreakpoints();
    });
  }

  private readonly pageMarkers = new BookPageMarkersUtils();

  constructor(
    private bookReaderApiV3: BookReaderApiV3Service,
    private sanitizer: DomSanitizer,
    private bookReaderService: BookReaderService,
    private spinner: SpinnerService,
    private route: ActivatedRoute,
    private router: Router,
    private encryptionService: EncryptionService,
    private elRef: ElementRef,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.sizeSubscription = this.bookReaderService.getSize().subscribe(
      size => {
        this.zoom = size
        setTimeout(() => {
          this.updateBreakpoints();
        })
      }
    )

    this.videoModalSubscription = this.videoModal.state.subscribe(open => {
      if (!open) this.selectedVideoSrc = null
    });

    this.bookUpdateObserver = new MutationObserver(_ => {
      // Flat page markers become page-number labels first, so everything below sees page-based content.
      if (this.pageMarkers.convertMarkers(this.contentContainer.nativeElement) > 0) {
        // The conversion of top-level markers is itself a child-list change; don't process it again.
        this.bookUpdateObserver.takeRecords();
      }
      this.prepareMedia();
      if (this.pendingScrollToTop) {
        this.pendingScrollToTop = false;
        this.contentContainer.nativeElement.scrollTop = 0;
      }
      this.updateBreakpointsWhenImagesLoaded();
    });
    this.bookUpdateObserver.observe(this.contentContainer.nativeElement,
      { attributes: false, childList: true, characterData: false });

    // Build the combined navigation across all `;`-joined sub-products first, then drive content off
    // the URL `anchor`. Fetching sub-product metadata here is what makes combined books (e.g. the
    // "Stages 3&4" teacher book) navigable end-to-end, like the old reader.
    this.loadStructure().pipe(
      switchMap(() => this.route.queryParams)
    ).subscribe(params => {
      const anchor = params['anchor']
      if (anchor == null) {
        // No anchor in the URL yet -> start at the first unit (manifest order) and reflect it there.
        if (this.units.length) this.updateAnchorInParams(this.units[0].key)
        return
      }
      const idx = this.units.findIndex(u => u.key === anchor)
      this.currentIndex = idx >= 0 ? idx : 0
      // Going back lands at the end of the target (pendingScrollToEnd / JUMP_TO_THE_LAST), anything else at its top.
      if (!this.pendingScrollToEnd && this.currentBreakpoint !== WebBrowserV2Component.JUMP_TO_THE_LAST) {
        this.pendingScrollToTop = true
        this.currentBreakpoint = 0
      }
      this.loadContent().subscribe()
    })
  }

  ngAfterViewChecked() {
    if (!this.tooltipReady) {
      const tooltips = this.elRef.nativeElement.querySelectorAll('[data-bs-toggle="tooltip"]');
      tooltips.forEach((el: HTMLElement) => {
        const title = el?.getAttribute('title') || el?.getAttribute('data-bs-original-title');
        if (title) {
          el.setAttribute('title', title);
          this.tooltipInstances.push(new bootstrap.Tooltip(el, { container: this.fullscreenContainer.nativeElement }));
        }
      });
      if(this.tooltipInstances.length == tooltips.length)
        this.tooltipReady = true;
    }
  }

  ngOnDestroy(): void {
    if(this.sizeSubscription) this.sizeSubscription.unsubscribe()
    if(this.chartResourcesSubscription) this.chartResourcesSubscription.unsubscribe()
    if(this.quizSubscription) this.quizSubscription.unsubscribe()
    if(this.videoModalSubscription) this.videoModalSubscription.unsubscribe()
    if(this.bookUpdateObserver) this.bookUpdateObserver.disconnect();
    this.tooltipInstances.forEach(t => t.dispose());
    this.tooltipInstances = [];
    this.resetChartState();
  }

  // The `;`-joined sub-product paths of a (possibly combined) book, e.g.
  // "en3/en3_3_online?variant=teacher;en3/en3_4_online?variant=teacher" -> two sub-products.
  private subPaths(): string[] {
    return (this._ebookPath ?? '').split(';').map(p => p.trim()).filter(p => p.length)
  }

  // Fetch each sub-product's ver-2 metadata (reusing the @Input metadata for the first one), then
  // build the combined navigation + table of contents.
  private loadStructure(){
    const paths = this.subPaths()
    if (!paths.length) return of(null)
    const fetches = paths.map((p, i) =>
      (i === 0 && this.metadata)
        ? of({ path: p, meta: this.metadata })
        : this.bookReaderApiV3.getMetadata(p).pipe(map(meta => ({ path: p, meta: meta as BookMetadataV2 })))
    )
    return this.spinner.trace(forkJoin(fetches).pipe(
      tap(subBooks => {
        this.metadataByPath.clear()
        subBooks.forEach(sub => this.metadataByPath.set(sub.path, sub.meta))
        this.buildUnits(subBooks)
        this.buildTocGroups(subBooks)
      })
    ))
  }

  // Flatten every sub-product's `contents` into the navigable sequence (anchors one by one, pages
  // page by page), in sub-product then manifest order. Pages are globally unique (continuous across
  // combined stages); anchor keys are prefixed with the sub-product index to stay unique.
  private buildUnits(subBooks: { path: string, meta: BookMetadataV2 }[]){
    const units: ReaderUnit[] = []
    const seenPages = new Set<number>()
    const multi = subBooks.length > 1
    subBooks.forEach((sub, subIndex) => {
      for (const entry of (sub.meta?.contents ?? [])) {
        if (!this.audienceAllows(entry) || entry.type === TGCHECKS_TYPE) continue
        if (entry.pages) {
          for (let p = entry.pages.start; p <= entry.pages.end; p++) {
            if (seenPages.has(p)) continue
            seenPages.add(p)
            units.push({ key: String(p), anchor: String(p), kind: 'page', path: sub.path })
          }
        } else if (entry.anchors && entry.anchors.length) {
          for (const a of entry.anchors) {
            units.push({ key: multi ? `${subIndex}_${a}` : a, anchor: a, kind: 'anchor', path: sub.path })
          }
        }
      }
    })
    this.units = units
    const pageNumbers = units.filter(u => u.kind === 'page').map(u => +u.anchor)
    this.firstPage = pageNumbers.length ? Math.min(...pageNumbers) : 0
    this.lastPage = pageNumbers.length ? Math.max(...pageNumbers) : 0
  }

  private currentKey(): string {
    return this.units[this.currentIndex]?.key
  }

  // An entry is shown only when its audience is empty (everyone) or contains the current variant.
  private audienceAllows(entry: BookContent): boolean {
    const audience = entry.audience
    if (!audience || audience.length === 0) return true
    const variant = this.getVariant()
    return !!variant && audience.includes(variant)
  }

  loadContent(){
    return this.spinner.trace(
      this.encryptionService.prepareEncryptionKeysIfNeeded().pipe(
        switchMap(_ => this.getEbookContent()),
        catchError(_ => {
          return this.encryptionService.refreshEncryptionKeys().pipe(
            switchMap(_ => this.getEbookContent()),
            catchError(_ => {
              return of(null)
            })
          )
        })
      )
    )
  }

  moveToNextPage(){
    if(this.currentIndex < this.units.length - 1) this.currentIndex += 1
    this.updateAnchorInParams(this.currentKey())
  }

  // Like "next": the previous unit opens at its top (only the up arrow at the top edge lands at its end).
  moveToPreviousPage(){
    if(this.currentIndex > 0) this.currentIndex -= 1
    this.updateAnchorInParams(this.currentKey())
  }

  getEbookContent(){
    const unit = this.units[this.currentIndex]
    if(!unit) return of(null)
    // Route to the unit's own sub-product (single path -> getContentV2 parses ?variant correctly)
    // and send the raw anchor/page number (not the disambiguated key) to the backend.
    return this.bookReaderApiV3.getContentV2(
        unit.anchor,
        unit.path,
        1,
        this.encryptionService.getDeviceKey(),
        this.encryptionService.getEncryptionKey())
        .pipe(
          switchMap(encryptedResponse => this.encryptionService.decrypt(encryptedResponse)),
          tap(response => {
            this.resetChartState();
            this.ebookContent = this.sanitizer.bypassSecurityTrustHtml(response)
          })
      )
  }

  showBookSizeSettings(){
    this.isBookSizeSettingsShown = !this.isBookSizeSettingsShown
  }

  changeTextSize(change: -1 | 1){
    this.bookReaderService.changeSize(change)
  }

  toggleFullscreen() {
    if (this.isFullscreen) {
      this.exitFullscreen();
      return;
    }
    this.enterFullscreen();
  }

  // Jump to a page (pages only — anchors are reached via next/prev or the table of contents).
  jumpToPage(){
    this.updateAnchorInParams(String(this.pageSelection))
    this.jumpToPageModal.hide()
  }

  // Table of contents: jump to the entry's unit (its key already resolves the right sub-product).
  // Reset to the first breakpoint so the target opens at the very beginning of its content
  // (otherwise the scroll position would carry over from where we were).
  goToEntry(key: string){
    if (key != null) {
      this.currentBreakpoint = 0
      this.updateAnchorInParams(key)
    }
    this.tocModal.hide()
  }

  // Unit key for a content entry's first anchor / page (anchor keys disambiguated per sub-product).
  private entryKey(entry: BookContent, subIndex: number, multi: boolean): string | null {
    if (entry.anchors && entry.anchors.length) return multi ? `${subIndex}_${entry.anchors[0]}` : entry.anchors[0]
    if (entry.pages) return String(entry.pages.start)
    return null
  }

  // Page range label for table-of-contents entries backed by pages (lessons/dictations).
  pageRange(entry: BookContent): string {
    if (!entry.pages) return ''
    const { start, end } = entry.pages
    return start === end ? `${start}` : `${start}–${end}`
  }

  // Group every sub-product's `contents` by type (first-appearance order) for a collapsible TOC.
  private buildTocGroups(subBooks: { path: string, meta: BookMetadataV2 }[]){
    const order: string[] = []
    const byType = new Map<string, { entry: BookContent; key: string }[]>()
    const multi = subBooks.length > 1
    subBooks.forEach((sub, subIndex) => {
      for (const entry of (sub.meta?.contents ?? [])) {
        if (!this.audienceAllows(entry) || entry.type === TGCHECKS_TYPE) continue
        const key = this.entryKey(entry, subIndex, multi)
        if (key == null) continue
        const type = entry.type ?? 'OTHER'
        if (!byType.has(type)) {
          byType.set(type, [])
          order.push(type)
        }
        byType.get(type)!.push({ entry, key })
      }
    })
    this.tocGroups = order.map(type => ({ type, label: this.typeLabel(type), entries: byType.get(type)! }))
  }

  // Make a manifest source path (e.g. "assets/charts/chart_1.1.jpg") relative to the resource base
  // (e.g. "assets"), because the /resources endpoint prepends the base — otherwise it doubles it (404).
  private toResourcePath(source: string, resources: string[]): string {
    const base = (resources ?? []).find(r => source === r || source.startsWith(r + '/'));
    return base ? source.slice(base.length).replace(/^\/+/, '') : source;
  }

  // A chart source given by the URL of the image (the eBook assets bucket) is shown from there; a path of the book
  // sources is fetched through the /resources endpoint.
  private isAbsoluteUrl(source: string): boolean {
    return /^https?:\/\//i.test(source);
  }

  // Localized (plural) group header for a content type, selected by the type, e.g.
  // "LESSON" -> EBOOKS.CONTENT_TYPES.LESSON -> "Lessons". Falls back to the raw type if untranslated.
  private typeLabel(type: string): string {
    const key = 'EBOOKS.CONTENT_TYPES.' + type
    const translated = this.translate.instant(key)
    return translated === key ? type : translated
  }


  private updateAnchorInParams(anchor: string){
    if (anchor == null) return
    this.router.navigate([], {
      queryParams: { anchor: anchor }
    }).then()
  }

  getZoom(){
    return {zoom: `${this.zoom}`};
  }

  getVariant() {
    // Variant is shared across a combined book's sub-products; read it from the first one.
    return this._ebookPath?.split(";")[0]?.split("=")[1]
  }

  private scrollDown(event: Event) {
    this.onMoveBreakpoint(1);
    event.preventDefault();
  }

  private scrollUp(event: Event) {
    this.onMoveBreakpoint(-1);
    event.preventDefault();
  }


  private onMoveBreakpoint(offset: number) {
    // ver-2 anchor content (intro, charts) has no pages (neither <page> nor page markers), so the page-based screenful breakpoints
    // don't apply — scroll the content container directly and change unit only at the edge.
    if (!this.pageMarkers.hasPages(this.contentContainer.nativeElement)) {
      if (this.scrollContentBy(offset)) return;
      // At the edge -> change unit. Going back should land at the END of the previous content:
      // JUMP_TO_THE_LAST covers a <page> target, pendingScrollToEnd (set in onMovePage) a page-less one.
      if (offset > 0) this.setInitialBreakpointAfterPageLoad(WebBrowserV2Component.JUMP_TO_THE_FIRST);
      else if (this.currentIndex + offset >= 0) this.setInitialBreakpointAfterPageLoad(WebBrowserV2Component.JUMP_TO_THE_LAST);
      this.onMovePage(offset);
      return;
    }
    if (this.currentIndex + offset > this.units.length - 1 && this.currentBreakpoint >= this.breakpointGroups.length - 1) return
    if (!this.moveBreakpoint(offset)) {
      if (offset > 0)
        this.setInitialBreakpointAfterPageLoad(WebBrowserV2Component.JUMP_TO_THE_FIRST);
      else if (this.currentIndex + offset >= 0) {
        this.setInitialBreakpointAfterPageLoad(WebBrowserV2Component.JUMP_TO_THE_LAST);
      }
      this.onMovePage(offset);
    }
  }

  // Scroll the content area by ~one screenful (for page-less content). Returns false when already at
  // the edge in the requested direction, so the caller moves to the next/previous unit instead.
  private scrollContentBy(offset: number): boolean {
    const el = this.contentContainer.nativeElement as HTMLElement;
    const step = el.clientHeight * 0.9;
    if (offset > 0) {
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 2) return false;
      el.scrollBy({ top: step, behavior: 'smooth' });
      return true;
    }
    if (el.scrollTop <= 2) return false;
    el.scrollBy({ top: -step, behavior: 'smooth' });
    return true;
  }

  private setInitialBreakpointAfterPageLoad(command: number) {
    if (command !== WebBrowserV2Component.JUMP_TO_THE_FIRST && command !== WebBrowserV2Component.JUMP_TO_THE_LAST) {
      throw new Error("the value isn't a command. Use JUMP_TO_THE_FIRST or JUMP_TO_THE_LAST");
    }
    this.currentBreakpoint = command;
  }

  private moveBreakpoint(offset: number): boolean {
    return this.jumpToBreakpoint(this.currentBreakpoint + offset);
  }

  private onMovePage(offset: number) {
    const target = this.currentIndex + offset
    if (target >= 0 && target <= this.units.length - 1) {
      this.currentIndex = target
      // Going back -> land at the end of the target content (handled after it loads).
      if (offset < 0) this.pendingScrollToEnd = true
      this.updateAnchorInParams(this.currentKey())
    }
  }

  // The screenful breakpoints (arrow keys) are computed from element offsets, but an image gets its height only
  // once it is loaded. Computed earlier, they are out of step with the layout as soon as the images arrive
  // (a page with large images, e.g. Teacher Guide Plus: the arrows jump too far, skip or repeat text). So they
  // are computed at once when no image is pending, otherwise when all of them have loaded (or failed).
  private updateBreakpointsWhenImagesLoaded() {
    const pending = Array.from(this.contentContainer.nativeElement.querySelectorAll('img') as NodeListOf<HTMLImageElement>)
      .filter(image => !image.complete);
    if (pending.length === 0) {
      this.updateBreakpoints();
      return;
    }
    let left = pending.length;
    const imageSettled = () => {
      if (--left === 0) this.updateBreakpoints();
    };
    pending.forEach(image => {
      image.addEventListener('load', imageSettled, { once: true });
      image.addEventListener('error', imageSettled, { once: true });
    });
  }

  private updateBreakpoints() {
    if (this.bookContainer.nativeElement.childCount == 0) return;
    this.calculateJumpBreakpoints();
    this.jumpToCurrentBreakpoint();
    if (this.pendingScrollToEnd) {
      this.pendingScrollToEnd = false;
      // For page-less content the breakpoint "JUMP_TO_THE_LAST" can't reach the end, so scroll the
      // container to the bottom directly (a <page> target is already handled by the breakpoints).
      if (!this.pageMarkers.hasPages(this.contentContainer.nativeElement)) this.scrollContentToEnd();
    }
  }

  // Book sources use a bare <video src="..."/>. A <video> is a replaced element (no pseudo-elements, so it
  // cannot be styled as a card in CSS) and a page can hold several clips, so each one becomes a card —
  // like the charts and quizzes — and the player opens in a modal on click.
  private prepareMedia(): void {
    this.contentContainer.nativeElement.querySelectorAll('video[src]').forEach((video: HTMLVideoElement) => {
      const card = document.createElement('div')
      card.className = 'video-card'
      card.setAttribute('data-video-src', video.getAttribute('src') ?? '')
      card.textContent = this.translate.instant('EBOOKS.VIDEO.WATCH')
      video.replaceWith(card)
    })
    // The cards are bare icons: the text is the accessible label only.
    this.contentContainer.nativeElement.querySelectorAll('.video-card[data-video-src], tgcheck[id]').forEach((card: HTMLElement) => {
      const label = card.textContent?.trim() ?? ''
      card.setAttribute('role', 'button')
      if (label) {
        card.setAttribute('title', label)
        card.setAttribute('aria-label', label)
      }
    })
  }

  openVideo(src: string) {
    if (!src) return
    this.selectedVideoSrc = src
    this.videoModal.show()
  }

  // Scroll page-less content to its bottom, re-applying once images finish loading (they grow the
  // scrollable height), so backwards navigation lands at the end rather than the start.
  private scrollContentToEnd() {
    const el = this.contentContainer.nativeElement as HTMLElement;
    const toEnd = () => { el.scrollTop = el.scrollHeight; };
    toEnd();
    el.querySelectorAll('img').forEach((img: HTMLImageElement) => {
      if (!img.complete) img.addEventListener('load', toEnd, { once: true });
    });
  }

  private calculateJumpBreakpoints() {
    const displayHeight = this.contentContainer.nativeElement.clientHeight * this.zoom;
    const keyElements = this.filterByChild(this.bookContainer.nativeElement
      .querySelectorAll('page-number, qa, hwinst, hwline, postqains, preqainst, img, lessonnumber, dict-cont, chart, table.headwords-like, subhwline, idiom, er, section-title, li, p'));
    let breakpointGroups: Array<BreakpointGroup> = [];
    let previousFrameBreakpoint = 0;
    let currentFrameBreakpoint = 0;
    let elementInFrame = 0;
    let currentBreakpointGroup: Array<any> = [];
    for (let i = 0 ; i < keyElements.length ; i++) {
      const currentKeyElement = keyElements[i];
      currentFrameBreakpoint = (currentKeyElement.offsetTop + currentKeyElement.offsetHeight) * this.zoom;
      elementInFrame++;
      currentBreakpointGroup.push(currentKeyElement);
      // break if elements is more than page over the previous
      if (currentFrameBreakpoint - previousFrameBreakpoint > displayHeight) {
        if (elementInFrame > 1) {
          // typical - rewind by 1 element
          i--;
          currentBreakpointGroup.splice(currentBreakpointGroup.length - 1, 1);
          currentFrameBreakpoint = (keyElements[i].offsetTop + keyElements[i].offsetHeight) * this.zoom;
        }
        // this is very specific case. If element is larger than screen size we keep it on the
        if (currentBreakpointGroup.length == 1 && currentBreakpointGroup[0].offsetHeight * this.zoom > displayHeight){
          const partials = Math.ceil( (currentBreakpointGroup[0].offsetHeight * this.zoom ) /  ( displayHeight * 0.8 ) ); //keep 20% additional space
          for (let partial = 0; partial < partials; partial++) {
            const partialGroup = new BreakpointGroup(currentBreakpointGroup);
            partialGroup.scrollOffset = partial;
            breakpointGroups.push(partialGroup);
          }
        } else {
          breakpointGroups.push(new BreakpointGroup(currentBreakpointGroup));
        }
        currentBreakpointGroup = [];
        previousFrameBreakpoint = currentFrameBreakpoint;
        elementInFrame = 0;
      }
    }
    if (currentBreakpointGroup.length > 0) {
      breakpointGroups.push(new BreakpointGroup(currentBreakpointGroup));
    }
    this.breakpointGroups = breakpointGroups;
  }

  private jumpToBreakpoint(value: number): boolean {
    if (value < 0 || value >= this.breakpointGroups.length) return false;
    this.currentBreakpoint = value;
    this.jumpToCurrentBreakpoint();
    return true;
  }

  private jumpToCurrentBreakpoint() {
    if (!this.breakpointGroups) return;
    if (this.currentBreakpoint < 0) {
      this.currentBreakpoint = 0;
      return;
    }
    if (this.currentBreakpoint >= this.breakpointGroups.length) {
      this.currentBreakpoint = this.breakpointGroups.length - 1;
      const breakpointGroup = this.breakpointGroups[this.currentBreakpoint];
      if (breakpointGroup == null || breakpointGroup.items.length === 0) return;
      breakpointGroup.items[0].scrollIntoView()
      return;
    }

    const breakpointGroup = this.breakpointGroups[this.currentBreakpoint];
    breakpointGroup.items[0].scrollIntoView({behavior: 'smooth'});
  }

  private filterByChild(keyElements: NodeList): any {
    const result = [];
    for (let i = 0 ; i < keyElements.length ; i++) {
      const element = keyElements[i] as any;
      if (element.querySelectorAll("li, p").length == 0) {
        result.push(element);
      }
    }
    return result;
  }

  // A <chart ref-id="..."> in the [innerHTML] content was clicked -> open its images in a modal.
  // Event delegation (bound once in the template) — no DOM manipulation, the backend markup is kept.
  onContentClick(event: MouseEvent) {
    const videoElement = (event.target as HTMLElement)?.closest('.video-card[data-video-src]');
    if (videoElement) {
      event.preventDefault();
      this.openVideo(videoElement.getAttribute('data-video-src') ?? '');
      return;
    }
    const quizElement = (event.target as HTMLElement)?.closest('tgcheck[id]');
    if (quizElement) {
      event.preventDefault();
      this.openQuiz(quizElement.getAttribute('id') ?? '');
      return;
    }
    const chartElement = (event.target as HTMLElement)?.closest('chart[ref-id]');
    if (!chartElement) return;
    event.preventDefault();
    this.openChart(chartElement.getAttribute('ref-id') ?? '');
  }

  // A <tgcheck id="..."> was clicked -> show that exercise from the book's checks file in a modal.
  openQuiz(quizId: string) {
    const stagePath = this.units[this.currentIndex]?.path;
    if (!stagePath || !quizId) return;
    this.quizSubscription?.unsubscribe();
    this.quizSubscription = this.loadQuizzes(stagePath).subscribe(quizzes => {
      const quiz = quizzes.get(quizId);
      if (!quiz) return;
      this.selectedQuiz = quiz;
      this.quizModal.show();
    });
  }

  private loadQuizzes(stagePath: string) {
    const cached = this.quizzesByPath.get(stagePath);
    if (cached) return of(cached);
    const anchor = (this.metadataByPath.get(stagePath)?.contents ?? [])
      .find(entry => entry.type === TGCHECKS_TYPE)?.anchors?.[0];
    if (!anchor) return of(new Map<string, Quiz>());
    // Same call as the page text: access-checked and encrypted with the device key.
    return this.spinner.trace(this.bookReaderApiV3.getContentV2(
      anchor, stagePath, 1, this.encryptionService.getDeviceKey(), this.encryptionService.getEncryptionKey()
    ).pipe(
      switchMap(encrypted => this.encryptionService.decrypt(encrypted)),
      map(xml => new QuizParser().parse(xml)),
      tap(quizzes => this.quizzesByPath.set(stagePath, quizzes)),
      catchError(() => of(new Map<string, Quiz>()))
    ));
  }

  openChart(refId: string) {
    const stagePath = this.units[this.currentIndex]?.path;
    if (!stagePath) return;
    const meta = this.metadataByPath.get(stagePath);
    const entry = this.resolveChartEntry(meta, refId);
    if (!entry) return;

    const sources = (entry.sources ?? []).map(source =>
      this.isAbsoluteUrl(source) ? source : this.toResourcePath(source, meta?.resources ?? []));

    this.releaseChartObjectUrls();
    this.selectedChartTitle = entry.title || refId || 'Chart';
    this.chartModal.show();
    this.loadChartImages(sources, stagePath);
  }

  // The content entry matched by ref-id — always present in the content — against the metadata
  // `refs`, regardless of entry type. Still limited to what's shown for the current variant and
  // to entries that actually have image sources.
  private resolveChartEntry(meta: BookMetadataV2 | undefined, refId: string): BookContent | null {
    const normalizedRefId = refId?.trim().toUpperCase();
    if (!normalizedRefId) return null;
    return (meta?.contents ?? []).find(entry =>
      this.audienceAllows(entry)
      && (entry.sources?.length ?? 0) > 0
      && (entry.refs ?? []).some(ref => ref?.trim().toUpperCase() === normalizedRefId)
    ) ?? null;
  }

  // `sources`: the URL of an image (shown as it is) or a resource path (fetched through the /resources endpoint).
  private loadChartImages(sources: string[], stagePath: string) {
    if (sources.length === 0) return;

    if (this.chartResourcesSubscription) {
      this.chartResourcesSubscription.unsubscribe();
    }

    const multiple = sources.length > 1;
    // spinner.trace drives the shared <spinner> (also shown inside the modal) via the global loading state.
    this.chartResourcesSubscription = this.spinner.trace(forkJoin(
      sources.map((source, index): Observable<ModalChartImage | null> => {
        const alt = multiple ? `${this.selectedChartTitle} (${index + 1})` : this.selectedChartTitle;
        if (this.isAbsoluteUrl(source)) return of({ alt, url: source });
        return this.bookReaderApiV3.getResource(stagePath, source).pipe(
          map(blob => this.createModalChartImage(blob, alt)),
          catchError(() => of(null))
        );
      })
    )).subscribe(images => {
      this.selectedChartImages = images.filter((image): image is ModalChartImage => image !== null);
    });
  }

  private createModalChartImage(blob: Blob, alt: string): ModalChartImage {
    const objectUrl = URL.createObjectURL(blob);
    this.chartObjectUrls.push(objectUrl);
    return { alt, url: objectUrl };
  }

  private releaseChartObjectUrls() {
    this.chartObjectUrls.forEach(url => URL.revokeObjectURL(url));
    this.chartObjectUrls = [];
    this.selectedChartImages = [];
  }

  private resetChartState() {
    if (this.chartResourcesSubscription) {
      this.chartResourcesSubscription.unsubscribe();
    }
    this.releaseChartObjectUrls();
    this.selectedChartTitle = 'Chart';
    if (this.chartModal?.isShown()) {
      this.chartModal.hide();
    }
  }

  private enterFullscreen() {
    const el = this.fullscreenContainer?.nativeElement as any;
    if (!el) return;
    const request = el.requestFullscreen || el.webkitRequestFullscreen;
    if (request) {
      request.call(el);
    }
  }

  private exitFullscreen() {
    const doc = document as any;
    const exit = doc.exitFullscreen || doc.webkitExitFullscreen;
    if (exit) {
      exit.call(doc);
    }
  }
}
